import { and, not, or } from 'tio-ui/utilities';
import { Checkbox, Select, Switch } from 'tio-ui/components/forms';
import { PhCheck, PhInfo, PhX } from 'ember-phosphor-icons';
import { on } from '@ember/modifier';
import { array, fn } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import ApplicationFilter from './filters/application.gts';
import Component from '@glimmer/component';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import PaymentFilter from './filters/payments.gts';
import SelectCompany from '../../select/company.gts';
import SelectEmployee from '../../select/employee.gts';
import type CompanyModel from '../../../models/company.ts';
import type EmployeeModel from '../../../models/employee.ts';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type TasProgramTemplateModel from '../../../models/tas-program-template.ts';
import type { ApplicationFilterSignature, PaymentFilterSignature } from './types';

interface AdvancedProgramFiltersSignature {
  Args: ApplicationFilterSignature &
    PaymentFilterSignature & {
      company?: CompanyModel;
      programTemplateId?: string;
      programCreatedAtStart?: string;
      programCreatedAtEnd?: string;
      excludeNonVoucherPrograms?: boolean;
      includeCourses?: boolean;
      coursesBeginDate?: string;
      coursesEndDate?: string;
      approverId?: string;
      employeeId?: string;
      terminatedEmployees?: boolean;
      excludeTermVerified?: boolean;
      includeInvoiceDate?: boolean;
      showIndividualApplications?: boolean;
      onFilterUpdate: (filterUpdates: Record<string, unknown>) => void;
      monthOffset?: number;
      isAdminContext?: boolean;
      onCompanyChange?: (company?: CompanyModel) => void;
    };
  Element: HTMLDivElement;
}

export class AdvancedProgramFilters extends Component<AdvancedProgramFiltersSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;

  get companyId() {
    return this.args.company?.id;
  }

  /***************************/
  /* Program Template Data   */
  /***************************/

  programsData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    if (!this.companyId) {
      return [];
    }
    return await this.store.query<TasProgramTemplateModel>('tas-program-template', {
      filter: {
        company: this.companyId,
      },
    });
  });

  get programOptions(): TasProgramTemplateModel[] {
    return this.programsData.value || [];
  }

  get programSelectOptions() {
    return this.programOptions.map((program: TasProgramTemplateModel) => ({
      label: program.programName,
      key: program.id,
    }));
  }

  /***************************/
  /* Partner Selection       */
  /***************************/

  companyDidChange = (company?: CompanyModel) => {
    // Call optional callback if provided
    if (this.args.onCompanyChange) {
      this.args.onCompanyChange(company);
    }
  };

  /***************************/
  /* Program Template Filters */
  /***************************/

  programTemplateDidChange = (programTemplateId: string | null) => {
    this.args.onFilterUpdate({ programTemplateId });
  };

  programCreatedAtStartDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ programCreatedAtStart: dateString });
  };

  programCreatedAtEndDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ programCreatedAtEnd: dateString });
  };

  /***************************/
  /* Course Filters          */
  /***************************/

  includeCoursesDidChange = (state: boolean) => {
    this.args.onFilterUpdate({
      includeCourses: state,
      coursesBeginDate: '',
      coursesEndDate: '',
    });
  };

  coursesBeginDateDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ coursesBeginDate: dateString });
  };

  coursesEndDateDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ coursesEndDate: dateString });
  };

  /***************************/
  /* Employee Filters        */
  /***************************/

  selectedEmployeeDidChange = (employee?: EmployeeModel) => {
    this.args.onFilterUpdate({ employeeId: employee?.id });
  };

  terminatedEmployeesDidChange = (state: boolean, _event: Event) => {
    this.args.onFilterUpdate({ terminatedEmployees: state });
  };

  excludeTermVerifiedDidChange = (state: boolean, _event: Event) => {
    this.args.onFilterUpdate({ excludeTermVerified: state });
  };

  /***************************/
  /* Approver Filters        */
  /***************************/

  @tracked approver?: EmployeeModel;

  get approverOptions() {
    return (
      this.approversData.value?.map((e) => ({
        label: e.fullName,
        key: e.id,
      })) || []
    );
  }

  approversData = trackedFunction(this, async () => {
    return this.store.adapterFor('company').getTasApprovers(this.companyId) as EmployeeModel[];
  });

  selectedApproverDidChange = (id: string | null) => {
    this.args.onFilterUpdate({ approverId: id });
  };

  /***************************/
  /* Invoice Filters         */
  /***************************/

  includeInvoiceDateDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ includeInvoiceDate: state });
  };

  /***************************/
  /* Program Filters         */
  /***************************/

  excludeNonVoucherProgramsDidChange = (value: boolean) => {
    this.args.onFilterUpdate({
      excludeNonVoucherPrograms: value,
    });
  };

  <template>
    <div class="flex flex-col gap-y-10 text-sm" ...attributes>
      {{! PARTNER SECTION - Admin Only in Global Context (no company pre-selected) }}
      {{#if (and @isAdminContext (not @company))}}
        <fieldset>
          <div class="flex flex-col gap-y-6">
            <div class="flex flex-col gap-y-3">
              <h3
                class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >Partner</h3>
              <div
                class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
              >
                <div class="flex items-start gap-2">
                  <PhInfo
                    @size="20"
                    class="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Testing Context
                    </h4>
                    <p class="text-xs text-blue-700 dark:text-blue-300">
                      Select a partner to preview their data and access partner-specific filters.
                      This selection is NOT saved with the report—it's for testing only.
                    </p>
                  </div>
                </div>
              </div>
              <SelectCompany
                @label="Filter by Partner"
                @onSelectionChange={{this.companyDidChange}}
                @selected={{@company.id}}
                @placeholder="All Partners"
                @inputSize="sm"
              />
            </div>
          </div>
        </fieldset>
      {{/if}}

      <fieldset>
        <div class="flex flex-col gap-y-6">
          {{! PROGRAMS SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >Programs</h3>
            <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
              <Select
                @isClearable={{true}}
                @items={{this.programSelectOptions}}
                @selectedKey={{@programTemplateId}}
                @label={{t "common.reporting.filter_by_program"}}
                @isLoading={{this.programsData.isLoading}}
                @onSelectionChange={{this.programTemplateDidChange}}
                @inputSize="sm"
              />
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-900 dark:text-gray-100">Program Creation
                  Date</label>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-gray-700 dark:text-gray-300">From</span>
                  <div class="flex-1 relative">
                    {{! @glint-expect-error: return to this, follow up with dev}}
                    <EmberFlatpickr
                      @date={{or @programCreatedAtStart ""}}
                      @onChange={{this.programCreatedAtStartDidChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      placeholder="mm/dd/yyyy"
                      class="tio-form-input"
                    />
                    {{#if @programCreatedAtStart}}
                      <button
                        type="button"
                        {{on "click" (fn this.programCreatedAtStartDidChange (array))}}
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label="Clear start date"
                      >
                        <PhX @size="16" />
                      </button>
                    {{/if}}
                  </div>
                  <span class="text-sm text-gray-700 dark:text-gray-300">To</span>
                  <div class="flex-1 relative">
                    {{! @glint-expect-error: return to this, follow up with dev}}
                    <EmberFlatpickr
                      @date={{or @programCreatedAtEnd ""}}
                      @onChange={{this.programCreatedAtEndDidChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      placeholder="mm/dd/yyyy"
                      @minDate={{or @programCreatedAtStart ""}}
                      class="tio-form-input"
                    />
                    {{#if @programCreatedAtEnd}}
                      <button
                        type="button"
                        {{on "click" (fn this.programCreatedAtEndDidChange (array))}}
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label="Clear end date"
                      >
                        <PhX @size="16" />
                      </button>
                    {{/if}}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {{! APPLICATIONS SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >Applications</h3>
            <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
              <ApplicationFilter
                @showIndividualApplications={{@showIndividualApplications}}
                @applicationStates={{@applicationStates}}
                @applicationCreatedAtStart={{@applicationCreatedAtStart}}
                @applicationCreatedAtEnd={{@applicationCreatedAtEnd}}
                @applicationDateRangePreset={{@applicationDateRangePreset}}
                @onFilterUpdate={{@onFilterUpdate}}
              />
            </div>
          </div>

          {{! PAYMENT DETAILS SECTION }}
          {{#if @showIndividualApplications}}
            <div class="flex flex-col gap-y-3">
              <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
                <PaymentFilter
                  @paymentYear={{@paymentYear}}
                  @monthOffset={{@monthOffset}}
                  @includePayments={{@includePayments}}
                  @paymentStates={{@paymentStates}}
                  @onFilterUpdate={{@onFilterUpdate}}
                />
              </div>
            </div>
          {{/if}}

          {{! OTHER FILTERS }}
          <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
            <Checkbox
              @label="Include Invoice Dates"
              @checked={{or @includeInvoiceDate false}}
              @onChange={{this.includeInvoiceDateDidChange}}
            />
          </div>

          <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
            <Checkbox
              @label="Include Course Details"
              @checked={{or @includeCourses false}}
              @onChange={{this.includeCoursesDidChange}}
            />
            <div
              class="{{if @includeCourses 'max-h-dvh overflow-visible' 'max-h-0 overflow-hidden'}}
                flex flex-col gap-2 ml-3 transition-all duration-500"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-700 dark:text-gray-300">From</span>
                <div class="flex-1 relative">
                  {{! @glint-expect-error: return to this, follow up with dev}}
                  <EmberFlatpickr
                    @date={{or @coursesBeginDate ""}}
                    @onChange={{this.coursesBeginDateDidChange}}
                    @dateFormat="Y-m-d"
                    @altInput={{true}}
                    @altFormat="F j, Y"
                    placeholder="mm/dd/yyyy"
                    class="tio-form-input"
                  />
                  {{#if @coursesBeginDate}}
                    <button
                      type="button"
                      {{on "click" (fn this.coursesBeginDateDidChange (array))}}
                      class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      aria-label="Clear begin date"
                    >
                      <PhX @size="16" />
                    </button>
                  {{/if}}
                </div>
                <span class="text-sm text-gray-700 dark:text-gray-300">To</span>
                <div class="flex-1 relative">
                  {{! @glint-expect-error: return to this, follow up with dev}}
                  <EmberFlatpickr
                    @date={{or @coursesEndDate ""}}
                    @onChange={{this.coursesEndDateDidChange}}
                    @dateFormat="Y-m-d"
                    @altInput={{true}}
                    @altFormat="F j, Y"
                    @minDate={{or @coursesBeginDate ""}}
                    placeholder="mm/dd/yyyy"
                    class="tio-form-input"
                  />
                  {{#if @coursesEndDate}}
                    <button
                      type="button"
                      {{on "click" (fn this.coursesEndDateDidChange (array))}}
                      class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      aria-label="Clear end date"
                    >
                      <PhX @size="16" />
                    </button>
                  {{/if}}
                </div>
              </div>
            </div>
          </div>

          {{! APPROVERS SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >Approvers</h3>
            <div class="flex flex-col gap-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
              <Select
                @label={{t "common.reporting.filter_by_approver"}}
                @items={{this.approverOptions}}
                @selectedKey={{@approverId}}
                @onSelectionChange={{this.selectedApproverDidChange}}
                @isClearable={{true}}
                @isDisabled={{not @showIndividualApplications}}
                @isLoading={{this.approversData.isLoading}}
                @inputSize="sm"
              />
            </div>
          </div>

          {{! EMPLOYEES SECTION }}
          <div class="flex flex-col gap-y-3">
            <h3
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >Employees</h3>
            <div class="flex flex-col gap-y-3 border-b border-gray-200 dark:border-gray-600 pb-3">
              {{#if this.companyId}}
                <SelectEmployee
                  @companyId={{this.companyId}}
                  @label={{t "common.reporting.filter_by_employee"}}
                  @selected={{@employeeId}}
                  @onChange={{this.selectedEmployeeDidChange}}
                  @labelClass="form-field-label text-black dark:text-white"
                  {{! @glint-expect-error: seems like a bug. this is forwarded to the div }}
                  disabled={{not @showIndividualApplications}}
                />
              {{/if}}
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{t
                    "common.reporting.terminated_employees"
                  }}</span>
                <Switch
                  @isSelected={{@terminatedEmployees}}
                  @onChange={{this.terminatedEmployeesDidChange}}
                  @intent="primary"
                >
                  <:thumbContent as |ctx|>
                    {{#if ctx.isSelected}}
                      <PhCheck @size="12" />
                    {{else}}
                      <PhX @size="12" />
                    {{/if}}
                  </:thumbContent>
                </Switch>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{t
                    "common.reporting.exclude_term_verified"
                  }}</span>
                <Switch
                  @isSelected={{@excludeTermVerified}}
                  @onChange={{this.excludeTermVerifiedDidChange}}
                  @intent="primary"
                >
                  <:thumbContent as |ctx|>
                    {{#if ctx.isSelected}}
                      <PhCheck @size="12" />
                    {{else}}
                      <PhX @size="12" />
                    {{/if}}
                  </:thumbContent>
                </Switch>
              </div>
            </div>
          </div>
          {{#if @excludeNonVoucherPrograms}}
            <div class="flex flex-col gap-y-3">
              <Checkbox
                @label="Voucher Programs Only"
                @checked={{or @excludeNonVoucherPrograms false}}
                @onChange={{this.excludeNonVoucherProgramsDidChange}}
              />
            </div>
          {{/if}}
        </div>
      </fieldset>
    </div>
  </template>
}
